<template>
    <vx-modal ref="modalRef"
        :padding="false"
        :stacked="stacked"
        v-model:visible="visible"
        @close="cancel()"
        size="lg">
        <template v-slot:header>
            <div class="p-6">
                {{ data.id ? 'Edit Message' : 'New Scheduled Message' }}
            </div>
        </template>

        <scheduled-message-content
            ref="contentRef"
            v-model="data"
            v-model:sendingError="sendingError"
            @selected:snippet="selectedSnippetBody = $event.body"
        />

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="cancel()"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="scheduleMessage()"
                    :loading="sending"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"> {{ data.id ? 'Update' : 'Create' }}
                    <font-awesome-icon widthAuto :icon="faSave"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import ScheduledMessageContent from '~/components/scheduled-messages/ScheduledMessageContent.vue';
import { VxButton, VxModal } from '@voxie/frontend-components';
import { actionsClient, scheduledMessagesClient } from '~/services';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, watch } from 'vue';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';
import useVuelidate from '@vuelidate/core';
import { getClientTz } from '~/utils/date';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    message: {
        type: Object,
    },
});

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const emit = defineEmits(['cancel', 'scheduled']);

const modalRef = ref();
const contentRef = ref();

const stacked = computed(() => contentRef.value?.stacked ?? false);

const sending = ref(false);
const selectedSnippetBody = ref('');
const sendingError = ref('');

const defaultMessage = () => ({
    id: undefined,
    body: '',
    media_url: undefined,
    media_content_type: undefined,
    send_at: dayjs().tz(getClientTz()).add(1, 'hour').toDate(),
    contact_id: null,
});

const data = ref(defaultMessage());

const validate = useVuelidate();

const { getCurrentGroupId } = useCurrentGroup();

const resetDefaults = () => {
    data.value = defaultMessage();
    sendingError.value = '';
    sending.value = false;
    selectedSnippetBody.value = '';
    validate.value.$reset();
};

const cancel = () => {
    emit('cancel');
};

const sendAtUtc = computed(() => toUTCDateTimeFormat(dayjs(data.value.send_at).tz(getClientTz())))

const createScheduleMessage = () => {
    return scheduledMessagesClient.createScheduledMessage({ teamId, groupId: getCurrentGroupId() }, data.value.contact_id, {
        body: data.value.body,
        send_at: sendAtUtc.value,
        media_url: data.value.media_url,
        media_content_type: data.value.media_content_type,
    });
};

const editScheduledMessage = async () => {
    try {
        await scheduledMessagesClient.deleteScheduledMessage({ teamId, groupId: getCurrentGroupId() }, data.value.contact_id, data.value.id);
    } catch (error) {
        if (error?.response?.status !== HTTP_NOT_FOUND) {
            throw error;
        }
    }
    return createScheduleMessage()
};

const scheduleMessage = async () => {
    if (sending.value) {
        return;
    }

    if (!(await validate.value.$validate())) {
        return;
    }

    sending.value = true;

    try {
        let response;
        if (!data.value.id) {
            response = await createScheduleMessage();
            toasted.success('Scheduled Message updated')
        } else {
            response = await editScheduledMessage();
            toasted.success('Scheduled Message updated')
        }

        if (selectedSnippetBody.value && data.value.body.includes(selectedSnippetBody.value)) {
            actionsClient.send('snippet_used');
        }

        emit('scheduled', response.data);
        visible.value = false;

        resetDefaults();
    } catch (e) {
        sendingError.value = catchRequestError(e);
        modalRef.value.scrollTo();
    } finally {
        sending.value = false;
    }
};

watch(
    () => visible.value,
    (opened) => {
        if (!opened) {
            resetDefaults();
            return;
        }

        if (props.message) {
            data.value = {
                id: props.message.id,
                body: props.message.body,
                send_at: dayjs.utc(props.message.send_at).tz(getClientTz()).toDate(),
                media_url: props.message.media_url,
                media_content_type: props.message.media_content_type,
                contact_id: props.message.contact_id,
            };
        }
    },
    {
        immediate: true,
        deep: true,
    }
);

</script>
